import { z } from 'zod';
import { USER_ROLES } from './enums';
/**
 * RBAC permission matrix — `resource:action` strings with a per-role map.
 * `platform_admin` is the true wildcard (`'*'`); `owner` is the company
 * wildcard: every permission, present and future, EXCEPT `PLATFORM_ONLY`.
 * Guards (API) and route/feature gating (panel) both consume THIS matrix;
 * neither may hard-code role checks. Role behaviour that is not a permission
 * (two-factor, sign-in surfaces, row scope, who may change whose role) is
 * data too: `ROLE_POLICY` and `ROLE_CHANGE_GRANTS` below.
 */
export const ALL_PERMISSIONS = [
    'company:read',
    'company:manage',
    'users:manage',
    'library:read',
    'library:write',
    'clients:read',
    'clients:write',
    'estimates:read',
    'estimates:write',
    'estimates:send',
    'proposals:read',
    'proposals:write',
    'scope-packages:read',
    'scope-packages:manage',
    'sub-quotes:read',
    'sub-quotes:write',
    'projects:read',
    'projects:manage',
    'work-logs:read',
    'work-logs:write',
    'materials:read',
    'materials:write',
    'materials:manage',
    'compliance:read',
    'dashboard:read',
    'files:read',
    'files:write',
    'exports:manage',
    'audit:read',
    'projects:create',
    'projects:invite',
    'users:read',
    'users:invite',
    'users:promote',
    'crews:read',
    'crews:manage',
    'schedule:read',
    'schedule:write',
    'time:read',
    'time:write',
    'time:manage',
    'costs:write',
    'finance:read',
    'platform:read',
    'platform:act_as',
    'dev:read',
    'assistant:use',
    'help:read',
];
export const PermissionSchema = z.enum(ALL_PERMISSIONS);
/** Every permission, present and future. Held only by `platform_admin`. */
export const OWNER_WILDCARD = '*';
/** Every permission, present and future, except `PLATFORM_ONLY`. Held by `owner`. */
export const COMPANY_WILDCARD = '*company';
/** Never granted by the company wildcard — platform operators only. */
export const PLATFORM_ONLY = ['platform:read', 'platform:act_as', 'dev:read'];
const COMPANY_PERMISSIONS = ALL_PERMISSIONS.filter((p) => !PLATFORM_ONLY.includes(p));
/**
 * The read half of the old estimator grant. Genuinely read-only — keep it that
 * way. It is no longer handed to the job-site roles wholesale (WORKER gets a
 * leaner read set); it survives so SUPERVISOR provably keeps everything the
 * estimator could read, and so a future viewer/auditor role can reuse it
 * without silently gaining the ability to write to a jobsite record.
 */
const READ_ONLY = [
    'company:read',
    'library:read',
    'clients:read',
    'estimates:read',
    'proposals:read',
    'scope-packages:read',
    'sub-quotes:read',
    'projects:read',
    'work-logs:read',
    'materials:read',
    'compliance:read',
    'dashboard:read',
    'files:read',
];
/**
 * The `worker` role (was `field`). Its write powers are limited to recording
 * what actually happened on site: checking a work item off, posting a work log
 * (with job photos — uploads ride `work-logs:write`, NOT `files:write`, Jonah
 * 2026-09-15), declaring a portion complete, adding to a materials list what
 * the job turned out to need, and logging its own time and costs. It
 * deliberately does NOT get `projects:manage`, so it can never define or reword
 * the work, add or remove a subcontractor, or accept a portion — the acts with
 * commercial consequence.
 *
 * Note the materials split: `materials:write` (add what we need) is a job-site
 * act; `materials:manage` (attach vendor pricing, import a list into an
 * estimate) is not, and stays with SUPERVISOR/FINANCIAL — it moves money.
 */
const WORKER = [
    'company:read',
    'library:read',
    'materials:read',
    'materials:write',
    'compliance:read',
    'projects:read',
    'work-logs:read',
    'work-logs:write',
    'files:read',
    'schedule:read',
    'time:write',
    'costs:write',
    'assistant:use',
    'help:read',
];
/** Runs a crew: WORKER plus reading the estimate, the people and crews, and managing the schedule and time. */
const FOREMAN = [
    ...WORKER,
    'estimates:read',
    'users:read',
    'crews:read',
    'schedule:write',
    'time:read',
    'time:manage',
];
/** Everything the retired `estimator` role held. SUPERVISOR must remain a superset of it. */
const ESTIMATOR_GRANT = [
    ...READ_ONLY,
    'library:write',
    'clients:write',
    'estimates:write',
    'estimates:send',
    'proposals:write',
    'scope-packages:manage',
    'sub-quotes:write',
    'projects:manage',
    'work-logs:write',
    'materials:write',
    'materials:manage',
    'files:write',
];
/** FOREMAN ∪ the old estimator grant, plus inviting/promoting people and managing crews. */
const SUPERVISOR = [
    ...new Set([
        ...FOREMAN,
        ...ESTIMATOR_GRANT,
        'projects:invite',
        'users:invite',
        'users:promote',
        'crews:manage',
        'time:read',
    ]),
];
/** The books: company-wide reads, pricing/library upkeep, and finance. No project creation, no field writes. */
const FINANCIAL = [
    'company:read',
    'library:read',
    'library:write',
    'clients:read',
    'estimates:read',
    'projects:read',
    'materials:read',
    'materials:manage',
    'files:read',
    'dashboard:read',
    'finance:read',
    'time:read',
    'assistant:use',
    'help:read',
    'compliance:read',
];
/** Role → permissions. Wildcards match every permission, present and future (the company wildcard minus PLATFORM_ONLY). */
export const ROLE_PERMISSIONS = {
    platform_admin: OWNER_WILDCARD,
    owner: COMPANY_WILDCARD,
    financial: FINANCIAL,
    supervisor: SUPERVISOR,
    foreman: FOREMAN,
    worker: WORKER,
};
/** Resolve the concrete permission list for a role (wildcards resolve against ALL_PERMISSIONS). */
export function permissionsForRole(role) {
    const entry = ROLE_PERMISSIONS[role];
    if (entry === OWNER_WILDCARD)
        return ALL_PERMISSIONS;
    if (entry === COMPANY_WILDCARD)
        return COMPANY_PERMISSIONS;
    return entry;
}
/** Can `role` perform `permission`? The true wildcard always passes; the company wildcard passes all but PLATFORM_ONLY. */
export function can(role, permission) {
    const entry = ROLE_PERMISSIONS[role];
    if (entry === OWNER_WILDCARD)
        return true;
    if (entry === COMPANY_WILDCARD)
        return !PLATFORM_ONLY.includes(permission);
    return entry.includes(permission);
}
export const ROLE_POLICY = {
    platform_admin: { totpMandatory: true, surfaces: ['panel', 'mobile'], scope: 'company' },
    owner: { totpMandatory: true, surfaces: ['panel', 'mobile'], scope: 'company' },
    financial: { totpMandatory: true, surfaces: ['panel', 'mobile'], scope: 'company' },
    supervisor: { totpMandatory: true, surfaces: ['panel', 'mobile'], scope: 'member' },
    foreman: { totpMandatory: true, surfaces: ['panel', 'mobile'], scope: 'crew' },
    worker: { totpMandatory: false, surfaces: ['mobile'], scope: 'crew' },
};
/** Every change within `set`: each role may move to any other role of the same set. */
function changesWithin(set) {
    return Object.fromEntries(set.map((from) => [from, set.filter((to) => to !== from)]));
}
/**
 * actor role → (current role → roles the actor may set). Covers promotion and
 * demotion alike (Jonah 2026-09-17): Admin any role; Owners move non-owners
 * among Supervisor/Financial/Foreman/Worker; Supervisors swap Worker ⇄ Foreman.
 */
export const ROLE_CHANGE_GRANTS = {
    platform_admin: changesWithin(USER_ROLES),
    owner: changesWithin(['supervisor', 'financial', 'foreman', 'worker']),
    financial: {},
    supervisor: { worker: ['foreman'], foreman: ['worker'] },
    foreman: {},
    worker: {},
};
/** May `actor` change a user's role from `from` to `to`? Unknown roles and no-op changes are refused. */
export function canChangeRole(actor, from, to) {
    if (from === to)
        return false;
    return ROLE_CHANGE_GRANTS[actor]?.[from]?.includes(to) ?? false;
}
/** Roles `actor` may move a `from`-role user to (empty when none). */
export function roleChangeTargets(actor, from) {
    return [...(ROLE_CHANGE_GRANTS[actor]?.[from] ?? [])];
}
/** Seniority used only to label a change Promote vs Demote — never to authorize one. */
export const ROLE_RANK = {
    worker: 0,
    foreman: 1,
    financial: 2,
    supervisor: 2,
    owner: 3,
    platform_admin: 4,
};
/** `true` when `to` outranks `from` (Promote); otherwise the change reads as a demotion. */
export function isPromotion(from, to) {
    return ROLE_RANK[to] > ROLE_RANK[from];
}
//# sourceMappingURL=permissions.js.map